import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';
import { Search, ShieldOff, Shield, Ban, CheckCircle, Eye, X, Edit2, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

const AdminUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [detail, setDetail] = useState(null);
  const [granting, setGranting] = useState(null); // { user_id, plan_type, note, is_lifetime }
  // iter117 — Prevents double-submits on the grant + revoke actions so a
  // hurried admin can't create duplicate subscriptions or trigger duplicate
  // emails by clicking the button twice while the request is in-flight.
  const [grantSubmitting, setGrantSubmitting] = useState(false);
  const [revokingSubId, setRevokingSubId] = useState(null);
  const [editing, setEditing] = useState(null); // { user_id, name, email, phone }
  // iter104 — Duplicate-email merge modal.
  //   mergeState = { open, email, plan, loading, committing, committed }
  // `plan` is the dry-run response from POST /admin/merge-duplicate-emails.
  const [mergeState, setMergeState] = useState({ open: false, email: '', plan: null, loading: false, committing: false, committed: false });

  const load = async () => {
    try {
      const res = await adminAPI.searchUsers({ q, role, limit: 200 });
      setUsers(res.data);
    } catch (e) { toast.error('Failed to load users'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const onSearchKey = (e) => { if (e.key === 'Enter') load(); };

  const toggleBlock = async (u) => {
    const next = !u.is_blocked;
    if (!window.confirm(`${next ? 'Block' : 'Unblock'} ${u.name || u.user_id}?`)) return;
    try { await adminAPI.toggleBlockUser(u.user_id, next); toast.success('Updated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const togglePromote = async (u) => {
    const next = !u.is_admin;
    if (!window.confirm(`${next ? 'Promote' : 'Demote'} ${u.name || u.user_id} ${next ? 'to admin' : 'from admin'}?`)) return;
    try { await adminAPI.setUserRole(u.user_id, next); toast.success('Updated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const openEdit = (u) => setEditing({ user_id: u.user_id, name: u.name || '', email: u.email || '', phone: u.phone || '', phone_verified: !!u.phone_verified });
  const submitEdit = async () => {
    try {
      await adminAPI.updateUser(editing.user_id, {
        name: editing.name, email: editing.email, phone: editing.phone, phone_verified: editing.phone_verified,
      });
      toast.success('User updated');
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const deleteUser = async (u) => {
    const label = u.name || u.email || u.user_id;
    const confirm1 = window.prompt(`Type DELETE to permanently remove "${label}" and all their data:`, '');
    if (confirm1 !== 'DELETE') { toast.error('Cancelled'); return; }
    try {
      const res = await adminAPI.deleteUser(u.user_id);
      toast.success(`Deleted user. ${res.data.listings_marked_deleted} listing(s) marked deleted.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const openDetail = async (u) => {
    try { const res = await adminAPI.getUserFull(u.user_id); setDetail(res.data); }
    catch (e) { toast.error('Failed to load user detail'); }
  };
  const submitGrant = async () => {
    if (!granting) return;
    if (grantSubmitting) return; // idempotency guard
    // "Lifetime" is a synthetic option in the dropdown → backend accepts
    // plan_type === "lifetime" and forces is_lifetime = true. We still pass
    // is_lifetime explicitly for older backends that keyed off the flag.
    const isLifetimeGrant = granting.plan_type === 'lifetime' || !!granting.is_lifetime;
    setGrantSubmitting(true);
    try {
      await adminAPI.grantSubscription(granting.user_id, granting.plan_type, granting.note, isLifetimeGrant);
      toast.success(isLifetimeGrant ? 'Lifetime subscription granted' : 'Subscription granted');
      setGranting(null);
      if (detail) openDetail({ user_id: granting.user_id });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setGrantSubmitting(false); }
  };

  // iter77 — Revoke any active subscription (regular OR admin-granted lifetime).
  const revokeSubscription = async (sub) => {
    if (revokingSubId === sub.subscription_id) return; // in-flight guard
    const label = sub.is_lifetime ? 'LIFETIME' : sub.plan_type;
    if (!window.confirm(`Revoke this ${label} subscription? The user will lose plan benefits immediately.`)) return;
    const note = window.prompt('Reason / note (optional):', '') || '';
    setRevokingSubId(sub.subscription_id);
    try {
      await adminAPI.deactivateSubscription(sub.subscription_id, note);
      toast.success('Subscription revoked');
      if (detail) openDetail({ user_id: detail.user.user_id });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setRevokingSubId(null); }
  };

  // iter104 — Duplicate-email merge handlers.
  const openMergeModal = (initialEmail = '') => {
    setMergeState({ open: true, email: initialEmail, plan: null, loading: false, committing: false, committed: false });
  };
  const closeMergeModal = () => {
    setMergeState({ open: false, email: '', plan: null, loading: false, committing: false, committed: false });
  };
  const runMergePlan = async () => {
    const email = mergeState.email.trim();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email address'); return; }
    setMergeState((s) => ({ ...s, loading: true, plan: null, committed: false }));
    try {
      const res = await adminAPI.mergeDuplicateEmails(email, false);
      setMergeState((s) => ({ ...s, loading: false, plan: res.data }));
      if (res.data?.duplicates && res.data.duplicates.length === 0) {
        toast.info(res.data.message || 'No duplicates to merge for this email');
      }
    } catch (e) {
      setMergeState((s) => ({ ...s, loading: false }));
      toast.error(e?.response?.data?.detail || 'Failed to fetch merge plan');
    }
  };
  const commitMerge = async () => {
    if (!window.confirm(`Merge ${mergeState.plan?.duplicates?.length || 0} duplicate account(s) into ${mergeState.plan?.canonical?.name || 'the canonical user'}? This cannot be undone.`)) return;
    setMergeState((s) => ({ ...s, committing: true }));
    try {
      const res = await adminAPI.mergeDuplicateEmails(mergeState.email.trim(), true);
      setMergeState((s) => ({ ...s, committing: false, plan: res.data, committed: true }));
      toast.success('Accounts merged successfully');
      load();
    } catch (e) {
      setMergeState((s) => ({ ...s, committing: false }));
      toast.error(e?.response?.data?.detail || 'Merge failed');
    }
  };


  return (
    <div className="space-y-3" data-testid="admin-users-tab">
      <div className="bg-white rounded-xl p-3 border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey}
              placeholder="Search by name, email, phone, user_id..."
              data-testid="users-search-input"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm outline-none focus:border-[#1800ad]" />
          </div>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="users-role-filter"
            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
            <option value="">All users</option>
            <option value="admin">Admins only</option>
            <option value="blocked">Blocked only</option>
          </select>
          <button onClick={load} className="bg-[#1800ad] text-white rounded-lg px-4 py-2 text-sm font-bold">Search</button>
          {/* iter104 — Merge duplicate accounts by email. Opens a modal that lets
              admin type any email (or seeds it from the current search query),
              runs a dry-run merge plan, then commits on confirm. */}
          <button
            type="button"
            onClick={() => openMergeModal(q.includes('@') ? q.trim() : '')}
            data-testid="open-merge-duplicates-btn"
            className="bg-white border-2 border-[#1800ad] text-[#1800ad] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5 whitespace-nowrap hover:bg-[#f0edff] transition-colors"
            title="Merge two or more accounts that share the same email"
          >
            <Users size={14} /> Merge duplicates
          </button>
        </div>
        <p className="mt-2 text-xs text-[#6B7280]">{users.length} result{users.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-[#f8f9fa] text-left">
            <tr>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2 hidden sm:table-cell">Phone</th>
              <th className="px-3 py-2 hidden md:table-cell">Joined</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-gray-100 hover:bg-[#fafafa]" data-testid={`user-row-${u.user_id}`}>
                <td className="px-3 py-2">
                  <p className="font-bold text-[#0A0B10] truncate max-w-[180px]">{u.name || '—'}</p>
                  <div className="flex items-center gap-1.5 max-w-[220px]">
                    <p className="text-[10px] text-[#6B7280] truncate">{u.email || u.user_id}</p>
                    {/* iter132 — Apple's "Hide My Email" private-relay marker.
                         Shown next to relay addresses (`*@privaterelay.appleid.com`)
                         so admins understand why the email looks like a random hash. */}
                    {(u.email_is_apple_relay || (u.email && u.email.endsWith('@privaterelay.appleid.com'))) && (
                      <span title="Apple Hide-My-Email relay address" data-testid={`apple-relay-badge-${u.user_id}`}
                        className="shrink-0 px-1.5 py-0.5 rounded bg-black/90 text-white text-[9px] font-bold uppercase tracking-wider">
                        Apple Relay
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 hidden sm:table-cell text-[#4B5563]">{u.phone || '—'}</td>
                <td className="px-3 py-2 hidden md:table-cell text-[#6B7280] text-xs">{u.created_at ? String(u.created_at).slice(0, 10) : ''}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 flex-wrap">
                    {u.is_admin && <span className="px-1.5 py-0.5 rounded bg-[#f0edff] text-[#1800ad] text-[10px] font-bold">ADMIN</span>}
                    {u.is_blocked && <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-[10px] font-bold">BLOCKED</span>}
                    {u.phone_verified && <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-600 text-[10px] font-bold">PH ✓</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1 flex-wrap justify-end">
                    <button onClick={() => openDetail(u)} title="View details" data-testid={`view-user-${u.user_id}`}
                      className="p-1.5 rounded bg-[#f0edff] text-[#1800ad] hover:bg-[#1800ad] hover:text-white">
                      <Eye size={13} />
                    </button>
                    <button onClick={() => openEdit(u)} title="Edit user" data-testid={`edit-user-${u.user_id}`}
                      className="p-1.5 rounded bg-[#fff4ed] text-[#ff8500] hover:bg-[#ff8500] hover:text-white">
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => togglePromote(u)} title={u.is_admin ? 'Demote' : 'Promote to admin'} data-testid={`role-user-${u.user_id}`}
                      className={`p-1.5 rounded ${u.is_admin ? 'bg-orange-50 text-orange-600 hover:bg-orange-600 hover:text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white'}`}>
                      {u.is_admin ? <ShieldOff size={13} /> : <Shield size={13} />}
                    </button>
                    {/* iter104 — Quick action: check/merge duplicates that share this user's email. */}
                    {u.email && (
                      <button onClick={() => openMergeModal(u.email)} title="Merge accounts sharing this email" data-testid={`merge-user-${u.user_id}`}
                        className="p-1.5 rounded bg-indigo-50 text-[#1800ad] hover:bg-[#1800ad] hover:text-white">
                        <Users size={13} />
                      </button>
                    )}
                    <button onClick={() => toggleBlock(u)} title={u.is_blocked ? 'Unblock' : 'Block'} data-testid={`block-user-${u.user_id}`}
                      className={`p-1.5 rounded ${u.is_blocked ? 'bg-green-50 text-green-600 hover:bg-green-600 hover:text-white' : 'bg-red-50 text-red-600 hover:bg-red-600 hover:text-white'}`}>
                      {u.is_blocked ? <CheckCircle size={13} /> : <Ban size={13} />}
                    </button>
                    <button onClick={() => deleteUser(u)} title="Delete user" data-testid={`delete-user-${u.user_id}`}
                      className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-700 hover:text-white">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-[#9CA3AF] text-sm">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* User detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-[#0A0B10]">{detail.user.name || detail.user.user_id}</h3>
                <p className="text-xs text-[#6B7280]">{detail.user.email} {detail.user.phone}</p>
              </div>
              <button onClick={() => setDetail(null)} className="p-1.5 rounded bg-gray-100 hover:bg-gray-200"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-4 text-xs sm:text-sm">
              <div className="bg-[#f8f9fa] rounded-lg p-3">
                <p><strong>Joined:</strong> {String(detail.user.created_at || '').slice(0, 10)}</p>
                <p><strong>Admin:</strong> {detail.user.is_admin ? 'Yes' : 'No'} | <strong>Blocked:</strong> {detail.user.is_blocked ? 'Yes' : 'No'}</p>
                <p><strong>Suspicious flags:</strong> {detail.user.suspicious_flags || 0}</p>
              </div>

              <div>
                <p className="font-bold text-[#0A0B10] mb-1.5">Listings ({detail.listings.length})</p>
                {(() => {
                  // Reconciliation helper: a listing's boost/pro is "paid" only if
                  // an add-on purchase for it carries a Razorpay payment id.
                  const paidBoost = new Set();
                  const paidPro = new Set();
                  (detail.addon_purchases || []).forEach((a) => {
                    // Razorpay-paid OR Apple-IAP-paid both count as a real payment.
                    if (!a.razorpay_payment_id && !a.apple_paid) return;
                    if (a.addon_id === 'pro_listing') paidPro.add(a.listing_id);
                    else paidBoost.add(a.listing_id);
                  });
                  return detail.listings.slice(0, 15).map((l) => (
                    <div key={l.listing_id} className="bg-[#fafafa] rounded p-2 mb-1.5 text-[11px]" data-testid={`user-listing-${l.listing_id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold text-[#0A0B10] truncate max-w-[240px]">{l.title}</span>
                        <span className="text-[#6B7280]">₹{l.price?.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap mt-1">
                        <span className="px-1.5 py-0.5 rounded bg-gray-200 text-[#4B5563] text-[9px] font-bold uppercase">{l.status}</span>
                        {l.is_boosted && (
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${paidBoost.has(l.listing_id) ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`} data-testid={`listing-boost-${l.listing_id}`}>
                            BOOSTED{paidBoost.has(l.listing_id) ? '' : ' · ⚠ NO PAYMENT'}
                          </span>
                        )}
                        {l.is_pro && (
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${paidPro.has(l.listing_id) ? 'bg-violet-50 text-[#1800ad]' : 'bg-red-50 text-red-700'}`} data-testid={`listing-pro-${l.listing_id}`}>
                            PRO{paidPro.has(l.listing_id) ? '' : ' · ⚠ NO PAYMENT'}
                          </span>
                        )}
                        {l.is_boosted && l.boost_expires_at && <span className="text-[9px] text-[#9CA3AF]">boost till {String(l.boost_expires_at).slice(0, 10)}</span>}
                        {l.is_pro && l.pro_expires_at && <span className="text-[9px] text-[#9CA3AF]">pro till {String(l.pro_expires_at).slice(0, 10)}</span>}
                      </div>
                      <p className="text-[9px] text-[#9CA3AF] mt-0.5 font-mono">{l.listing_id}</p>
                    </div>
                  ));
                })()}
                {detail.listings.length === 0 && <p className="text-[11px] text-[#9CA3AF]">No listings.</p>}
              </div>

              <div>
                <p className="font-bold text-[#0A0B10] mb-1.5">Subscriptions ({detail.subscriptions.length})</p>
                {detail.subscriptions.slice(0, 10).map((s) => (
                  <div key={s.subscription_id} className="bg-[#fafafa] rounded p-2 mb-1.5 text-[11px] flex items-center justify-between gap-2 flex-wrap" data-testid={`sub-row-${s.subscription_id}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-[#0A0B10]">{s.plan_name || s.plan_type}</span>
                      {' · '}{s.is_lifetime ? <span className="font-bold text-emerald-700">No Expiry</span> : <>expires {String(s.expires_at || '').slice(0, 10)}</>}
                      {' · '}{s.is_active ? 'active' : 'expired'}
                      <span className="block text-[9px] text-[#9CA3AF]">purchased {String(s.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                      {s.is_lifetime && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[9px] font-bold" data-testid={`lifetime-badge-${s.subscription_id}`}>LIFETIME</span>}
                      {s.granted_by_admin && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[9px] font-bold">MANUAL</span>}
                    </div>
                    {s.is_active && (
                      <button
                        onClick={() => revokeSubscription(s)}
                        disabled={revokingSubId === s.subscription_id}
                        data-testid={`revoke-sub-${s.subscription_id}`}
                        className="bg-red-50 hover:bg-red-100 text-red-700 rounded px-2 py-1 text-[10px] font-bold whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        {revokingSubId === s.subscription_id && (
                          <span className="inline-block w-2.5 h-2.5 border-2 border-red-300 border-t-red-700 rounded-full animate-spin" />
                        )}
                        {revokingSubId === s.subscription_id ? 'Revoking…' : 'Revoke'}
                      </button>
                    )}
                  </div>
                ))}
                {detail.subscriptions.length === 0 && <p className="text-[11px] text-[#9CA3AF]">No subscriptions.</p>}
              </div>

              {/* Payment tracking — real Razorpay records for reconciliation */}
              <div data-testid="user-payments-section">
                <p className="font-bold text-[#0A0B10] mb-1.5">Payment Tracking ({(detail.payments || []).length})</p>
                <p className="text-[10px] text-[#9CA3AF] mb-1.5">Copy the Razorpay Payment ID to reconcile against your Razorpay Dashboard.</p>
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="w-full text-[10px] sm:text-[11px]">
                    <thead className="bg-[#f8f9fa] text-left text-[#6B7280]">
                      <tr>
                        <th className="px-2 py-1.5 whitespace-nowrap">Date/Time</th>
                        <th className="px-2 py-1.5 whitespace-nowrap">Amount</th>
                        <th className="px-2 py-1.5 whitespace-nowrap">Item</th>
                        <th className="px-2 py-1.5 whitespace-nowrap">Method</th>
                        <th className="px-2 py-1.5 whitespace-nowrap">Razorpay Order ID</th>
                        <th className="px-2 py-1.5 whitespace-nowrap">Razorpay Payment/Txn ID</th>
                        <th className="px-2 py-1.5 whitespace-nowrap">Listing</th>
                        <th className="px-2 py-1.5 whitespace-nowrap">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.payments || []).map((p) => (
                        <tr key={p.razorpay_order_id || p.payment_id} className="border-t border-gray-100 align-top" data-testid={`payment-row-${p.razorpay_order_id || p.payment_id}`}>
                          <td className="px-2 py-1.5 whitespace-nowrap text-[#4B5563]">{String(p.captured_at || p.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap font-semibold">₹{(p.amount_inr ?? (p.amount || 0) / 100).toLocaleString()}</td>
                          <td className="px-2 py-1.5 max-w-[130px] truncate" title={p.item_label || p.purpose}>{p.item_label || p.purpose || '—'}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap">{p.method || 'Razorpay'}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap font-mono select-all" data-testid={`payment-order-id-${p.razorpay_order_id || p.payment_id}`}>{p.razorpay_order_id || '—'}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap font-mono select-all" data-testid={`payment-txn-id-${p.razorpay_order_id || p.payment_id}`}>{p.razorpay_payment_id || <span className="text-red-600">none</span>}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[#6B7280]">{p.associated_listing_id ? String(p.associated_listing_id).slice(-8) : '—'}</td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${p.status === 'captured' ? 'bg-green-50 text-green-700' : p.status === 'created' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{p.status || '—'}</span>
                          </td>
                        </tr>
                      ))}
                      {(detail.payments || []).length === 0 && (
                        <tr><td colSpan={8} className="px-2 py-3 text-center text-[#9CA3AF]">No payment records.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Add-on purchases (Pro / Boost) mapped to listings */}
                {(detail.addon_purchases || []).length > 0 && (
                  <div className="mt-2" data-testid="user-addons-section">
                    <p className="font-bold text-[#0A0B10] mb-1">Add-on Purchases ({detail.addon_purchases.length})</p>
                    {detail.addon_purchases.slice(0, 15).map((a) => (
                      <div key={a.purchase_id} className="bg-[#fafafa] rounded p-2 mb-1 text-[10px] flex items-center justify-between gap-2 flex-wrap" data-testid={`addon-row-${a.purchase_id}`}>
                        <span className="font-semibold text-[#0A0B10]">{a.addon_name || a.addon_id} · ₹{a.price}</span>
                        <span className="font-mono text-[#6B7280]">listing …{String(a.listing_id || '').slice(-8)}</span>
                        {a.razorpay_payment_id
                          ? <span className="font-mono text-[#4B5563]">{a.razorpay_payment_id}</span>
                          : a.apple_paid
                            ? <span className="font-bold text-emerald-700" data-testid={`addon-apple-paid-${a.purchase_id}`}>Paid via Apple IAP</span>
                            : <span className="font-mono text-red-600 font-bold">⚠ no payment id</span>}
                        <span className="text-[#9CA3AF]">till {String(a.expires_at || '').slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>


              <div className="bg-[#f0edff] rounded-lg p-3">
                <p className="font-bold text-[#1800ad] mb-2">Grant subscription</p>
                {!granting || granting.user_id !== detail.user.user_id ? (
                  <button onClick={() => setGranting({ user_id: detail.user.user_id, plan_type: '7_day', note: '', is_lifetime: false })}
                    data-testid="open-grant-form" className="bg-[#1800ad] text-white rounded px-3 py-1.5 text-xs font-bold">Open grant form</button>
                ) : (
                  <div className="space-y-2">
                    <select value={granting.plan_type} onChange={(e) => setGranting({ ...granting, plan_type: e.target.value, is_lifetime: e.target.value === 'lifetime' })} data-testid="grant-plan"
                      className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs">
                      <option value="day_pass">Starter Plan</option>
                      <option value="7_day">7-Day Plan</option>
                      <option value="15_day">15-Day Plan</option>
                      <option value="30_day">30-Day Plan</option>
                      <option value="power_seller">Power Seller Plan</option>
                      <option value="lifetime">Lifetime (Unlimited · No Expiry)</option>
                    </select>
                    <input value={granting.note} onChange={(e) => setGranting({ ...granting, note: e.target.value })}
                      placeholder="Reason / note (optional)" data-testid="grant-note"
                      className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" />
                    {granting.plan_type === 'lifetime' ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-[11px] text-emerald-800" data-testid="grant-lifetime-info">
                        <span className="font-bold">Lifetime access.</span> Never expires. Unlimited ads &amp; boosts. Any existing active subscription will be replaced. Can be revoked any time.
                      </div>
                    ) : (
                      /* iter77 — Optional Lifetime override for any base plan (kept for backwards compatibility). */
                      <label className="flex items-start gap-2 text-[11px] text-[#0A0B10] cursor-pointer select-none bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="grant-lifetime-row">
                        <input
                          type="checkbox"
                          checked={!!granting.is_lifetime}
                          onChange={(e) => setGranting({ ...granting, is_lifetime: e.target.checked })}
                          data-testid="grant-lifetime-checkbox"
                          className="mt-0.5 accent-emerald-600"
                        />
                        <span>
                          <span className="font-bold text-emerald-800">Mark as Lifetime / Unlimited</span>
                          <span className="block text-[10px] text-emerald-700/80">Overrides the selected plan — no expiry, unlimited ads.</span>
                        </span>
                      </label>
                    )}
                    <div className="flex gap-2">
                      <button onClick={submitGrant} data-testid="grant-submit" className={`flex-1 rounded px-3 py-1.5 text-xs font-bold text-white ${(granting.plan_type === 'lifetime' || granting.is_lifetime) ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#1800ad]'}`}>
                        {(granting.plan_type === 'lifetime' || granting.is_lifetime) ? 'Grant Lifetime' : 'Grant'}
                      </button>
                      <button onClick={() => setGranting(null)} className="bg-gray-200 text-[#4B5563] rounded px-3 py-1.5 text-xs font-bold">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit user modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
            <div className="border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="font-bold text-[#0A0B10]">Edit user</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded bg-gray-100"><X size={14} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div>
                <label className="text-xs font-bold text-[#4B5563]">Name</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  data-testid="edit-user-name" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#4B5563]">Email</label>
                <input value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  data-testid="edit-user-email" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#4B5563]">Phone</label>
                <input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  placeholder="+91…" data-testid="edit-user-phone" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]" />
              </div>
              <label className="flex items-center gap-2 text-xs font-bold text-[#0A0B10] bg-gray-50 rounded-lg px-3 py-2">
                <input type="checkbox" checked={!!editing.phone_verified}
                  onChange={(e) => setEditing({ ...editing, phone_verified: e.target.checked })} data-testid="edit-user-verified" />
                Mark phone as verified
              </label>
              <div className="flex gap-2 pt-1">
                <button onClick={submitEdit} data-testid="edit-user-save" className="flex-1 bg-[#1800ad] hover:bg-[#1200a0] text-white rounded-lg px-4 py-2 font-bold">Save</button>
                <button onClick={() => setEditing(null)} className="bg-gray-200 text-[#4B5563] rounded-lg px-4 py-2 font-bold">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iter104 — Merge Duplicate Emails modal.
          Two-step flow:
            1) Admin types an email → clicks Preview → backend returns dry-run plan.
            2) Admin reviews canonical + duplicates → clicks Merge → backend commits.
          The button on the toolbar and the per-row Users icon both open this. */}
      {mergeState.open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) closeMergeModal(); }}
          data-testid="admin-merge-modal"
        >
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Users size={18} className="text-[#1800ad]" />
                <h3 className="text-base font-bold text-[#0A0B10]">Merge duplicate accounts by email</h3>
              </div>
              <button onClick={closeMergeModal} className="p-1.5 rounded bg-gray-100 hover:bg-gray-200" data-testid="admin-merge-close">
                <X size={16} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={mergeState.email}
                  onChange={(e) => setMergeState((s) => ({ ...s, email: e.target.value, plan: null, committed: false }))}
                  placeholder="user@example.com"
                  data-testid="admin-merge-email-input"
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm outline-none focus:border-[#1800ad]"
                />
                <button
                  type="button"
                  onClick={runMergePlan}
                  disabled={mergeState.loading || mergeState.committing}
                  data-testid="admin-merge-preview-btn"
                  className="bg-[#1800ad] text-white rounded-lg px-4 py-2 text-sm font-bold hover:bg-[#1200a0] disabled:opacity-50 whitespace-nowrap"
                >
                  {mergeState.loading ? 'Loading…' : 'Preview'}
                </button>
              </div>

              {mergeState.plan && (
                <div className="space-y-3">
                  {mergeState.committed && (
                    <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800" data-testid="admin-merge-success">
                      ✓ Merge completed. The canonical user now holds all data below.
                    </div>
                  )}
                  {(!mergeState.plan.duplicates || mergeState.plan.duplicates.length === 0) && !mergeState.committed && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                      {mergeState.plan.message || 'No duplicates found for this email.'}
                    </div>
                  )}
                  {mergeState.plan.canonical && (
                    <div>
                      <p className="text-xs font-bold uppercase text-[#6B7280] mb-1">Canonical (will be KEPT)</p>
                      <div className="rounded-lg bg-[#f0edff] border border-[#1800ad]/30 px-3 py-2 text-sm">
                        <div className="font-semibold text-[#0A0B10]">{mergeState.plan.canonical.name || '(no name)'} · <span className="text-xs font-normal text-[#6B7280]">{mergeState.plan.canonical.user_id}</span></div>
                        <div className="text-xs text-[#4B5563] mt-1">
                          {mergeState.plan.canonical.phone || 'no phone'} · created {String(mergeState.plan.canonical.created_at || '').slice(0,10)}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {mergeState.plan.canonical.google_sub && <span className="px-1.5 py-0.5 rounded text-[10px] bg-white border border-gray-200">Google</span>}
                          {mergeState.plan.canonical.apple_sub && <span className="px-1.5 py-0.5 rounded text-[10px] bg-white border border-gray-200">Apple</span>}
                          {mergeState.plan.canonical.phone_verified && <span className="px-1.5 py-0.5 rounded text-[10px] bg-white border border-gray-200">📱 verified</span>}
                          {mergeState.plan.canonical.email_verified && <span className="px-1.5 py-0.5 rounded text-[10px] bg-white border border-gray-200">✉ verified</span>}
                        </div>
                      </div>
                    </div>
                  )}
                  {mergeState.plan.duplicates && mergeState.plan.duplicates.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase text-[#6B7280] mb-1">Will be merged INTO canonical ({mergeState.plan.duplicates.length})</p>
                      <div className="space-y-2">
                        {mergeState.plan.duplicates.map((d) => (
                          <div key={d.user_id} className="rounded-lg bg-white border border-gray-200 px-3 py-2 text-sm">
                            <div className="font-semibold text-[#0A0B10]">{d.name || '(no name)'} · <span className="text-xs font-normal text-[#6B7280]">{d.user_id}</span></div>
                            <div className="text-xs text-[#4B5563] mt-1">
                              {d.phone || 'no phone'} · created {String(d.created_at || '').slice(0,10)}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {d.google_sub && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-50 border border-gray-200">Google</span>}
                              {d.apple_sub && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-50 border border-gray-200">Apple</span>}
                              {d.phone_verified && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-50 border border-gray-200">📱 verified</span>}
                              {d.email_verified && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-50 border border-gray-200">✉ verified</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={closeMergeModal}
                className="px-4 py-2 text-sm rounded-lg bg-white border border-gray-200 hover:border-gray-300 text-[#4B5563]"
                data-testid="admin-merge-cancel"
              >
                Close
              </button>
              {mergeState.plan && mergeState.plan.duplicates && mergeState.plan.duplicates.length > 0 && !mergeState.committed && (
                <button
                  type="button"
                  onClick={commitMerge}
                  disabled={mergeState.committing}
                  data-testid="admin-merge-commit-btn"
                  className="px-4 py-2 text-sm rounded-lg bg-[#ff8500] hover:bg-[#e57500] text-white font-bold disabled:opacity-50"
                >
                  {mergeState.committing ? 'Merging…' : `Merge ${mergeState.plan.duplicates.length} account(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminUsersTab;
